/**
 * useCrmDocuments — generic document storage hook for any CRM entity
 * (contacts today, deals later). Wraps the manageCrmDocuments callable:
 *
 *   { action: 'list',           entityType, entityId }        → { documents }
 *   { action: 'requestUpload',  entityType, entityId, ...,
 *     withThumbnail }                                          → { documentId, storagePath, token, mimeType, thumbnailPath?, thumbnailToken? }
 *   { action: 'finalizeUpload', documentId }                  → { success, document }
 *   { action: 'getDownloadUrl', documentId, disposition }     → { url, fileName, mimeType }
 *   { action: 'rename',         documentId, fileName }        → { success, document }
 *   { action: 'delete',         documentId }                  → { success }
 *   { action: 'purgeEntity',    entityType, entityId }        → { success, deleted }
 *
 * Upload pipeline per file: requestUpload → PUT the bytes straight to the
 * private crm-documents bucket via uploadToSignedUrl (presigned token — no
 * Supabase session needed) → finalizeUpload activates the metadata row.
 *
 * All access control is server-side (Firebase auth → CRM role → live entity
 * ownership). Hooks return { success, error } — toasts belong to the caller.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../utils/init-firebase';
import { supabase } from '../utils/supabase';
import { DOCUMENTS_BUCKET, validateFileForUpload } from '../constants/documentConstants';
import { canGenerateThumbnail, generateThumbnail } from '../components/CRM/Documents/thumbnailUtils';

const functions = getFunctions(app, 'us-east1');

function callDocuments(payload) {
  const call = httpsCallable(functions, 'manageCrmDocuments');
  return call(payload);
}

/**
 * Delete every document attached to an entity (files + metadata). Standalone
 * (not hook-bound) so flows like contact deletion can call it directly.
 * IMPORTANT: call BEFORE deleting the entity row — the backend ownership
 * check needs the entity to still exist.
 */
export async function purgeEntityDocuments(entityType, entityId) {
  try {
    const res = await callDocuments({ action: 'purgeEntity', entityType, entityId });
    return { success: true, deleted: res.data?.deleted ?? 0 };
  } catch (err) {
    console.error('manageCrmDocuments purgeEntity failed:', err);
    // code lets callers distinguish "nothing to purge / function unavailable"
    // (functions/not-found) from real failures that should block deletion.
    return { success: false, error: err.message || 'Failed to delete documents', code: err.code || null };
  }
}

export function useCrmDocuments(entityType, entityId, { enabled = true } = {}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]); // [{ key, name, size }]
  const [busyDocIds, setBusyDocIds] = useState([]);
  // Which entity we've already fetched — avoids re-invoking the function
  // every time the tab regains focus, while refetching when the entity changes.
  const loadedKeyRef = useRef(null);

  const entityKey = entityType && entityId ? `${entityType}:${entityId}` : null;

  const refresh = useCallback(async () => {
    if (!entityKey) return;
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await callDocuments({ action: 'list', entityType, entityId });
      setDocuments(res.data?.documents || []);
      loadedKeyRef.current = entityKey;
    } catch (err) {
      console.error('manageCrmDocuments list failed:', err);
      if (err.code === 'functions/permission-denied') {
        setAccessDenied(true);
      }
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [entityKey, entityType, entityId]);

  // Reset when switching entities so stale documents never flash.
  useEffect(() => {
    if (loadedKeyRef.current && loadedKeyRef.current !== entityKey) {
      loadedKeyRef.current = null;
      setDocuments([]);
      setError(null);
      setAccessDenied(false);
    }
  }, [entityKey]);

  // Lazy initial fetch: only once the caller marks the hook enabled
  // (e.g. the Documents tab is first opened).
  useEffect(() => {
    if (enabled && entityKey && loadedKeyRef.current !== entityKey && !loading) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entityKey, refresh]);

  const markBusy = (docId, busy) => {
    setBusyDocIds((prev) => (busy ? [...prev, docId] : prev.filter((id) => id !== docId)));
  };

  /**
   * Upload one or more File objects. Sequential so results map cleanly.
   * Returns { success, results: [{ fileName, success, error?, document? }] }.
   */
  const uploadFiles = useCallback(
    async (files, { category = 'general' } = {}) => {
      const fileList = Array.from(files || []);
      if (!fileList.length || !entityKey) {
        return { success: false, results: [] };
      }
      if (!supabase) {
        return {
          success: false,
          results: fileList.map((f) => ({ fileName: f.name, success: false, error: 'Storage is not configured' })),
        };
      }

      const results = [];
      for (const file of fileList) {
        const key = `${Date.now()}_${file.name}`;
        const precheck = validateFileForUpload(file);
        if (!precheck.ok) {
          results.push({ fileName: file.name, success: false, error: precheck.error });
          continue;
        }

        setUploadingFiles((prev) => [...prev, { key, name: file.name, size: file.size }]);
        try {
          // 1. Ask the backend for a presigned upload slot (validates
          //    name/size/type + entity access, creates the pending row).
          //    withThumbnail requests a second slot for a 320px preview.
          const reqRes = await callDocuments({
            action: 'requestUpload',
            entityType,
            entityId,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type || '',
            category,
            withThumbnail: canGenerateThumbnail(file),
          });
          const { documentId, storagePath, token, mimeType, thumbnailPath, thumbnailToken } =
            reqRes.data || {};
          if (!documentId || !storagePath || !token) {
            throw new Error('Upload could not be initialized');
          }

          // 2. PUT the bytes straight to the private bucket.
          const { error: uploadError } = await supabase.storage
            .from(DOCUMENTS_BUCKET)
            .uploadToSignedUrl(storagePath, token, file, {
              contentType: mimeType || file.type || 'application/octet-stream',
            });
          if (uploadError) {
            throw new Error(uploadError.message || 'Upload to storage failed');
          }

          // 2b. Best-effort thumbnail: generate + upload to the second slot.
          //     Any failure just means no preview — never fails the upload.
          if (thumbnailPath && thumbnailToken) {
            try {
              const thumbBlob = await generateThumbnail(file);
              if (thumbBlob) {
                const { error: thumbError } = await supabase.storage
                  .from(DOCUMENTS_BUCKET)
                  .uploadToSignedUrl(thumbnailPath, thumbnailToken, thumbBlob, {
                    contentType: 'image/jpeg',
                  });
                if (thumbError) {
                  console.warn(`Thumbnail upload failed (${file.name}):`, thumbError.message);
                }
              }
            } catch (thumbErr) {
              console.warn(`Thumbnail step failed (${file.name}):`, thumbErr?.message || thumbErr);
            }
          }

          // 3. Activate the row (backend verifies the object really exists
          //    and stamps thumbnail_path if the preview landed).
          const finRes = await callDocuments({ action: 'finalizeUpload', documentId });
          results.push({ fileName: file.name, success: true, document: finRes.data?.document });
        } catch (err) {
          console.error(`Document upload failed (${file.name}):`, err);
          results.push({ fileName: file.name, success: false, error: err.message || 'Upload failed' });
        } finally {
          setUploadingFiles((prev) => prev.filter((f) => f.key !== key));
        }
      }

      await refresh();
      return { success: results.every((r) => r.success), results };
    },
    [entityKey, entityType, entityId, refresh]
  );

  /** Short-lived signed URL. disposition: 'inline' (view) | 'download'. */
  const getUrl = useCallback(async (documentId, disposition = 'download') => {
    markBusy(documentId, true);
    try {
      const res = await callDocuments({ action: 'getDownloadUrl', documentId, disposition });
      return { success: true, ...res.data };
    } catch (err) {
      console.error('manageCrmDocuments getDownloadUrl failed:', err);
      return { success: false, error: err.message || 'Could not get file link' };
    } finally {
      markBusy(documentId, false);
    }
  }, []);

  const rename = useCallback(async (documentId, fileName) => {
    markBusy(documentId, true);
    try {
      const res = await callDocuments({ action: 'rename', documentId, fileName });
      const updated = res.data?.document;
      if (updated) {
        setDocuments((prev) => prev.map((d) => (d.id === documentId ? updated : d)));
      }
      return { success: true, document: updated };
    } catch (err) {
      console.error('manageCrmDocuments rename failed:', err);
      return { success: false, error: err.message || 'Rename failed' };
    } finally {
      markBusy(documentId, false);
    }
  }, []);

  const remove = useCallback(async (documentId) => {
    markBusy(documentId, true);
    try {
      await callDocuments({ action: 'delete', documentId });
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      return { success: true };
    } catch (err) {
      console.error('manageCrmDocuments delete failed:', err);
      return { success: false, error: err.message || 'Delete failed' };
    } finally {
      markBusy(documentId, false);
    }
  }, []);

  return {
    documents,
    loading,
    error,
    accessDenied,
    uploadingFiles,
    busyDocIds,
    refresh,
    uploadFiles,
    getUrl,
    rename,
    remove,
  };
}

export default useCrmDocuments;
